
'use client';

import axios from 'axios';
import Cookies from 'js-cookie';
import { UserProfile, useUserStore } from '@/store/user-store';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;
const LANDING_API_BASE_URL = process.env.NEXT_PUBLIC_LANDING_API_BASE_URL;
const LOGIN_URL = process.env.NEXT_PUBLIC_API_LOGIN_URL;
const SIGNUP_URL = process.env.NEXT_PUBLIC_API_SIGNUP_URL;

export const fetchAndSetUser = async (router: AppRouterInstance) => {
    try {
        const { data: response, status } = await axios.get<{ data: UserProfile, status: string, message: string }>(`${API_BASE_URL}/api/user/me`, {
            withCredentials: true
        });
        if (response.status !== 'success' || status !== 200) {
            throw new Error(response.message);
        }
        useUserStore.getState().setUser(response.data);
    } catch (error) {
        console.error("Failed to fetch user profile:", error);
        // Clear user state on error
        useUserStore.getState().setUser(null);
        Cookies.remove('token');
        localStorage.removeItem('token');
        router.replace('/signin');
    }
}

export const signIn = async (credentials: any, router: AppRouterInstance) => {
    try {
        const res = await axios.post(`${LOGIN_URL}`, credentials, {
            withCredentials: true
        });
        if (res.data.token) {
            Cookies.set('token', res.data.token, { expires: 7, path: '/' });
            localStorage.setItem("token", res.data.token);
            await fetchAndSetUser(router); // Fetch and store user profile
        }
        return res.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message || 'Sign in failed');
        }
        throw new Error('An unexpected error occurred during sign in.');
    }
};

export const signUp = async (userData: any) => {
    try {
        const res = await axios.post(`${SIGNUP_URL}`, userData, {
            withCredentials: true
        });
        if (res.data.token) {
            Cookies.set('token', res.data.token, { expires: 7, path: '/' });
            localStorage.setItem("token", res.data.token);
        }
        return res.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message || 'Sign up failed');
        }
        throw new Error('An unexpected error occurred during sign up.');
    }
};

export const verifyOtp = async (otp: string) => {
    try {
        const res = await axios.post(`${API_BASE_URL}/api/onboard/verify-otp`, { otp }, {
            withCredentials: true
        });
        if (res.data.token) {
            Cookies.set('token', res.data.token, { expires: 7, path: '/' });
            localStorage.setItem("token", res.data.token);
        }
        return res.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message || 'OTP verification failed');
        }
        throw new Error('An unexpected error occurred during OTP verification.');
    }
};

export const resendOtp = async () => {
    try {
        const res = await axios.post(`${API_BASE_URL}/api/onboard/resend-otp`, {}, {
            withCredentials: true
        });
        return res.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message || 'Failed to resend OTP');
        }
        throw new Error('An unexpected error occurred while resending OTP.');
    }
};

export const resetPassword = async (email: string) => {
    try {
        const res = await axios.post(`${API_BASE_URL}/api/reset-password`, { email });
        return res.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message || 'Failed to send password reset link.');
        }
        throw new Error('An unexpected error occurred during password reset.');
    }
}

export const validateResetToken = async (token: string) => {
    try {
        const res = await axios.get(`${API_BASE_URL}/api/verify-reset-password-token`, {
            params: { token },
            withCredentials: true
        });
        return res.data;

    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message || 'Token validation failed.');
        }
        throw new Error(error.message || 'An unexpected error occurred during token validation.');
    }
};

export const updatePassword = async ({ passwordResetHash, password, repeatPassword, email }: { passwordResetHash: string, password: any, repeatPassword: any, email: string }) => {
    try {
        const res = await axios.post(`${API_BASE_URL}/api/update-password`, { passwordResetHash, password, repeatPassword, email }, {
            withCredentials: true
        });
        return res.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message || 'Password update failed.');
        }
        throw new Error('An unexpected error occurred during password update.');
    }
};
interface ChangePasswordParams {
    currentPassword: string;
    password: string;
    repeatPassword: string;
}

export const changePassword = async (data: ChangePasswordParams) => {
    try {
        const res = await axios.put(`${API_BASE_URL}/api/user/change-password`, data, {
            withCredentials: true
        });
        return res.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message || 'Password update failed.');
        }
        throw new Error('An unexpected error occurred during password update.');
    }
};


export const signOut = () => {
    Cookies.remove('token', { path: '/' });
    localStorage.removeItem("token");
};